import {ParentScannerProps, ScannerMode} from './type';
import React, {useCallback, useEffect, useState} from 'react';

import {Barcode} from 'scandit-react-native-datacapture-barcode';
import ExpectedBarcodes from './ExpectedBarcodes';
import If from '@buncha/components/conditional/If';
import {StyleSheet} from 'react-native';
import {PTextInput} from '@buncha/components/core/input/TextInput';
import {getOs} from '@buncha/utils/appInfo';
import {scaleFonts} from '@buncha/appStyles/style/font';
import {useColorThemeType} from '@buncha/appStyles/hooks/theme';
import Box from '@buncha/components/core/view/Box';
import {checkPriceDecimals} from '@buncha/utils/common';
import {BottomButton} from '../Button';

const Scanner = (props: ParentScannerProps) => {
  const {mode, onScan, triggerAddAnother} = props;
  const [allBarcodes, setAllBarcodes] = useState<{[key: string]: boolean}>({});
  const [enteredBarcode, setEnteredBarcode] = useState<number | null>();
  const [savedBarcode, setSavedBarcode] = useState<number | null>();
  const theme = useColorThemeType();

  const isSingleScanScanner =
    mode !== ScannerMode.matrixScan && mode !== ScannerMode.continuousScan;

  let expectedBarcodes: string[] | undefined;
  if (!isSingleScanScanner || mode === ScannerMode.singleScan)
    expectedBarcodes = props.expectedBarcodes;

  const handleExpectedBarcodes = useCallback(() => {
    setAllBarcodes(prevBarcodes => {
      const newBarcodes = {...prevBarcodes};
      const existingBarcode = expectedBarcodes?.find(
        code =>
          Number(code) === Number({data: enteredBarcode}) ||
          code === String(enteredBarcode),
      );
      if (existingBarcode) newBarcodes[existingBarcode] = true;
      const barcodeToSend =
        String(enteredBarcode)?.length < 7
          ? String(enteredBarcode) + '0'
          : String(enteredBarcode);
      const barcode = {data: barcodeToSend} as Barcode;
      if (mode === ScannerMode.continuousScan)
        onScan(barcode, !Object.values(newBarcodes).includes(false));
      else if (mode === ScannerMode.continuousSingleScan) onScan(barcode);
      else if (mode === ScannerMode.singleScan) onScan(barcode, '');
      return newBarcodes;
    });
    setSavedBarcode(Number(enteredBarcode ?? ''));
  }, [enteredBarcode, expectedBarcodes, mode, onScan]);

  useEffect(() => {
    if (expectedBarcodes)
      setAllBarcodes(prev => {
        const updated = {...prev};
        expectedBarcodes.forEach(barcode => {
          if (!(barcode in updated)) updated[barcode] = false;
        });
        return updated;
      });
  }, [expectedBarcodes]);

  useEffect(() => {
    setSavedBarcode(null);
    setEnteredBarcode(null);
  }, [triggerAddAnother]);

  useEffect(() => {
    expectedBarcodes?.forEach(barcode => {
      if (!allBarcodes[barcode]) allBarcodes[barcode] = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedBarcodes]);

  const handleChange = useCallback((count: string) => {
    if (checkPriceDecimals(count)) setEnteredBarcode(Number(count));
  }, []);

  return (
    <React.Fragment>
      <Box style={styles.scannerContainer}>
        <If condition={expectedBarcodes?.length}>
          <ExpectedBarcodes
            expectedBarcodes={expectedBarcodes || []}
            allBarcodes={allBarcodes}
          />
        </If>
        <PTextInput
          value={String(enteredBarcode ? enteredBarcode : '')}
          placeholder="Enter Barcode"
          textAlignVertical={'top'}
          clearInput
          debounce={false}
          returnKeyType="done"
          style={[styles.inputBox, {color: theme?.text?.blue?.[300]}]}
          onChangeText={handleChange}
          containerStyle={[
            styles.inputContainer,
            {
              borderColor: theme?.text?.blue?.[300],
            },
          ]}
          keyboardType="number-pad"
        />
        <BottomButton
          style={styles.buttonContainer}
          title={'Confirm Barcode'}
          onPress={handleExpectedBarcodes}
          disabled={!enteredBarcode || savedBarcode === enteredBarcode}
        />
      </Box>
    </React.Fragment>
  );
};

export default React.memo(Scanner);

const styles = StyleSheet.create({
  inputContainer: {
    borderWidth: 1,
    height: 51,
    width: '100%',
    borderRadius: 10,
    marginTop: 10,
    marginBottom: 20,
  },
  inputBox: {marginTop: getOs() === 'android' ? scaleFonts(20, 'height') : 0},
  buttonContainer: {
    width: '100%',
    marginTop: 'auto',
  },
  scannerContainer: {padding: 20},
});
